import firebase from "firebase";
import Vue from "vue";

import { Dispatcher } from "@/store/helper/firestore";

import _ from "lodash";
import uuid from "uuid";
import status_resolver from "@/utils/status_resolver";
import user_resolver from "@/utils/user_resolver";
import { createAuditItemPath, fieldPath } from "@/utils/firestorePathHelper";

const SET_AUDITS_UNLISTENER = "SET_AUDITS_UNLISTENER";
const SET_AUDITS = "SET_AUDITS";
const SET_AUDITS_METADATA = "SET_AUDITS_METADATA";
const CLEAR_AUDITS = "CLEAR_AUDITS";

const INVALID_MUTATION = null;

export const api = {
  namespace: "audits",
  getters: {
    createNewAudit: "createNewAudit",
    loadedAudits: "loadedAudits",
    getFinalWorkflowStatusId: "getFinalWorkflowStatusId",
    getFinalMeasureWorkflowStatusId: "getFinalMeasureWorkflowStatusId",
    getAudits: "getAudits",
    getTodos: "getTodos",
    getDefaultCalcSchemaName: "getDefaultCalcSchemaName",
    getDialogItem: "getDialogItem",
    getDialogAction: "getDialogAction",
    getAuditId: "getAuditId",
    getAuditItemId: "getAuditItemId",
    getDeviationId: "getDeviationId",
    getMediaId: "getMediaId",
    getMeasureId: "getMeasureId",
    getMeasures: "getMeasures",
    getMeasuresByAuditId: "getMeasuresByAuditId",
    getMappedMeasures: "getMappedMeasures",
    getMeasureById: "getMeasureById",
    getMappedAudits: "getMappedAudits",
    getFindingMeasureMapping: "getFindingMeasureMapping",
    getMeasureIdsByFindingId: "getMeasureIdsByFindingId",
    getAuditById: "getAuditById",
    getFlatAuditMatrix: "getFlatAuditMatrix",
    getFlatMeasureMatrix: "getFlatMeasureMatrix"
  },
  mutations: {
    resetState: "resetState",
    SET_AUDITS_UNLISTENER,
    SET_AUDITS,
    SET_AUDITS_METADATA,
    CLEAR_AUDITS,
    setloadedMeasures: "setloadedMeasures",
    createMeasure: "createMeasure",
    updateMeasure: "updateMeasure",
    deleteMeasure: "deleteMeasure",
    createTemplate: "createTemplate",

    setDialogItem: "setDialogItem",
    setDialogAction: "setDialogAction",
    setAuditId: "setAuditId",
    setAuditItemId: "setAuditItemId",
    setDeviationId: "setDeviationId",
    setMediaId: "setMediaId",
    setMeasureId: "setMeasureId"
  },
  actions: {
    loadAudits: "loadAudits",
    clearAudits: "clearAudits",
    loadMeasures: "loadMeasures",
    updateAudit: "updateAudit",
    appendAttachmentToAudit: "appendAttachmentToAudit",
    updateAuditStatus: "updateAuditStatus",
    deleteAudit: "deleteAudit",

    removeFileFromFinding: "removeFileFromFinding",
    uploadMedia: "uploadMedia",
    createAuditItem: "createAuditItem",
    updateAuditItem: "updateAuditItem",
    deleteAuditItem: "deleteAuditItem",
    createMeasure: "createMeasure",
    updateMeasure: "updateMeasure",
    deleteMeasure: "deleteMeasure",
    createTemplate: "createTemplate",
    setDialogItem: "setDialogItem",
    setDialogAction: "setDialogAction",
    setAuditId: "setAuditId",
    setAuditItemId: "setAuditItemId",
    setDeviationId: "setDeviationId",
    setMediaId: "setMediaId",
    setMeasureId: "setMeasureId"
  },
  state: {
    loadedAudits: "loadedAudits",
    loadedAuditsUnlistener: "loadedAuditsUnlistener",
    loadedMeasures: "loadedMeasures",
    currentDialogItem: "currentDialogItem",
    currentDialogAction: "currentDialogAction",
    currentAuditId: "currentAuditId",
    currentAuditItemId: "currentAuditItemId",
    currentDeviationId: "currentDeviationId",
    currentMediaId: "currentMediaId",
    currentMeasureId: "currentMeasureId"
  }
};

const defaultState = () => {
  return {
    [api.state.loadedAudits]: {},
    [api.state.loadedAuditsUnlistener]: null,
    [api.state.loadedMeasures]: [],
    [api.state.currentDialogItem]: {
      geolocation: null,
      text: "",
      importance: "",
      findingperson: {
        id: "",
        displayName: "",
        email: ""
      },
      riskassessment: "",
      reason: "",
      solution: "",
      media: []
    },
    [api.state.currentDialogAction]: null,
    [api.state.currentAuditId]: null,
    [api.state.currentAuditItemId]: null,
    [api.state.currentDeviationId]: null,
    [api.state.currentMediaId]: null,
    [api.state.currentMeasureId]: null
  };
};

const state = defaultState();

const mutations = {
  [api.mutations.resetState](state) {
    Object.assign(state, defaultState());
  },
  [api.mutations.setDialogItem](state, payload) {
    state.currentDialogItem = payload;
  },
  [api.mutations.setDialogAction](state, payload) {
    state.currentDialogAction = payload;
  },
  [api.mutations.setAuditId](state, payload) {
    state.currentAuditId = payload;
  },
  [api.mutations.setAuditItemId](state, payload) {
    state.currentAuditItemId = payload;
  },
  [api.mutations.setDeviationId](state, payload) {
    state.currentDeviationId = payload;
  },
  [api.mutations.setMediaId](state, payload) {
    state.currentMediaId = payload;
  },
  [api.mutations.setMeasureId](state, payload) {
    state.currentMeasureId = payload;
  },

  [api.mutations.SET_AUDITS_UNLISTENER](state, payload) {
    const prev_unlistener = state.loadedAuditsUnlistener;
    Vue.set(state, "loadedAuditsUnlistener", payload);
    if (prev_unlistener) {
      prev_unlistener();
    }
  },
  [api.mutations.SET_AUDITS](state, { removeAudits, modifiedAudits }) {
    removeAudits.forEach(auditId => {
      Vue.delete(state[api.state.loadedAudits], auditId);
    });

    modifiedAudits.forEach(auditDoc => {
      Vue.set(state[api.state.loadedAudits], auditDoc.id, auditDoc.data);
    });
  },
  [api.mutations.SET_AUDITS_METADATA](state, auditMetadataUpdate) {
    auditMetadataUpdate.forEach(doc => {
      const audit = state[api.state.loadedAudits][doc.id];
      if (audit) {
        audit.exists = doc.exists;
        audit.fromCache = doc.metadata.fromCache;
        audit.hasPendingWrites = doc.metadata.hasPendingWrites;
      }
    });
  },
  [api.mutations.CLEAR_AUDITS](state, payload) {
    const prev_unlistener = state[api.state.loadedAuditsUnlistener];
    // To prevent that problems with the execution order of the components lifecycle hook will messup the audits watcher
    if ((payload && payload === prev_unlistener) || payload === null) {
      Vue.set(state, api.state.loadedAuditsUnlistener, null);
      Vue.set(state, api.state.loadedAudits, {});
      if (prev_unlistener) {
        prev_unlistener();
      }
    }
  },
  [api.mutations.setloadedMeasures](state, payload) {
    state.loadedMeasures = payload;
  },
  [api.mutations.createMeasure](state, payload) {
    const measure_id = payload.measure_id;

    state.loadedMeasures.push({ ...payload.data, id: measure_id });
  },
  [api.mutations.updateMeasure](state, payload) {
    const data = payload.data;
    const measure_id = payload.measure_id;

    Object.assign(
      state.loadedMeasures.find(measure => {
        return measure.id === measure_id;
      }),
      data
    );
  },
  [api.mutations.deleteMeasure](state, payload) {
    const measure_id = payload.measure_id;

    const measure_index = state.loadedMeasures.findIndex(
      measure => measure.id === measure_id
    );

    state.loadedMeasures.splice(measure_index, 1);
  }
};

const actions = {
  async [api.actions.setDialogItem]({ commit }, payload) {
    commit("setDialogItem", _.cloneDeep(payload));
  },
  async [api.actions.setDialogAction]({ commit }, payload) {
    commit("setDialogAction", payload);
  },
  async [api.actions.setAuditId]({ commit }, payload) {
    commit("setAuditId", payload);
  },
  async [api.actions.setAuditItemId]({ commit }, payload) {
    commit("setAuditItemId", payload);
  },
  async [api.actions.setDeviationId]({ commit }, payload) {
    commit("setDeviationId", payload);
  },
  async [api.actions.setMediaId]({ commit }, payload) {
    commit("setMediaId", payload);
  },
  async [api.actions.setMeasureId]({ commit }, payload) {
    commit("setMeasureId", payload);
  },
  async [api.actions.loadMeasures]({ commit, dispatch }) {
    dispatch("app/setLoading", false, { root: true });

    try {
      const snapshot = await firebase
        .firestore()
        .collection("measures")
        .get();
      const MISSING_NAME = "Audit not present anymore";

      const measures = [];

      snapshot.forEach(doc => {
        let measure = doc.data();
        measures.push({
          audit_name: null,
          audit_id: null,
          ...measure,
          id: doc.id
        });
      });

      for (let measure of measures) {
        try {
          if (
            measure.auditRef instanceof Object &&
            typeof measure.auditRef.id === "string" &&
            typeof measure.auditRef.name === "string"
          ) {
            measure.audit_name = measure.auditRef.name;
            measure.audit_id = measure.auditRef.id;
          } else if (
            typeof measure.audit_id === "string" &&
            measure.audit_name === null
          ) {
            const res = await firebase
              .firestore()
              .collection("audits")
              .doc(measure.audit_id)
              .get();
            if (res.exists) {
              const auditData = res.data();
              measure.audit_name = auditData.name || MISSING_NAME;
            } else {
              measure.audit_name = MISSING_NAME;
            }
          } else if (measure.audit_name === null) {
            measure.audit_name = MISSING_NAME;
          }
        } catch (err) {
          console.error(err);
          dispatch(
            "app/addStatus",
            {
              message: err.message,
              type: "error"
            },
            { root: true }
          );
        }
      }

      commit(api.mutations.setloadedMeasures, measures);

      dispatch("app/setLoading", false, { root: true });
      return true;
    } catch (err) {
      console.err(err);
      dispatch(
        "app/addStatus",
        {
          message: err.message,
          type: "error"
        },
        { root: true }
      );
      dispatch("app/setLoading", false, { root: true });
      return false;
    }
  },
  async [api.actions.loadAudits]({ commit, dispatch, getters }, payload) {
    console.assert(payload, "loadAudits require a payload");
    if (payload) {
      dispatch("app/setLoading", false, { root: true });
      let query = firebase.firestore().collection("audits");
      if (payload.year) {
        query = query.where("planning_year", "==", payload.year);
      }
      if (payload.user) {
        query = query.where("leadauditor", "==", payload.user);
      }

      if (payload.status) {
        query = query.where("workflow.status", "<", payload.status);
      }

      if (payload.limit) {
        query = query.limit(payload.limit);
      }

      const default_calc_schema = getters.getDefaultCalcSchemaName;

      const observer = {
        complete: () => {},
        error: error => {
          console.error("AUDITS:load ERROR", error);
          dispatch("app/setLoading", false, { root: true });
        },
        next: querySnapshot => {
          const docChanges = querySnapshot.docChanges({
            // includeMetadataChanges: true
          });

          if (docChanges.length > 0) {
            const removeAudits = docChanges
              .filter(docChange => docChange.type === "removed")
              .map(docChange => docChange.doc.id);

            const modifiedAudits = docChanges
              .filter(docChange => docChange.type !== "removed")
              .map(docChange => {
                const doc = docChange.doc;
                const audit = doc.data();
                audit.id = doc.id;
                audit.exists = doc.exists;
                audit.fromCache = doc.metadata.fromCache;
                audit.hasPendingWrites = doc.metadata.hasPendingWrites;

                if (typeof audit.summary !== "string") {
                  audit.summary = "";
                }
                if (typeof audit.reportSchema !== "string") {
                  audit.reportSchema = default_calc_schema;
                }

                return {
                  data: audit,
                  metadata: {
                    ...doc.metadata
                  },
                  exists: doc.exists,
                  id: doc.id
                };
              });

            commit(api.mutations.SET_AUDITS, { removeAudits, modifiedAudits });
            dispatch("app/setLoading", false, { root: true });
          } else {
            const updateAuditMetadata = querySnapshot.docs.map(doc => {
              return {
                id: doc.id,
                exists: doc.exists,
                metadata: doc.metadata
              };
            });

            /* audit.id = doc.id;
          audit.exists = doc.exists;
          audit.fromCache = doc.metadata.fromCache;
          audit.hasPendingWrites = doc.metadata.hasPendingWrites; */

            commit(api.mutations.SET_AUDITS_METADATA, updateAuditMetadata);
          }
        }
      };
      const unlistener = query.onSnapshot(
        { includeMetadataChanges: true },
        observer
      );
      commit(api.mutations.SET_AUDITS_UNLISTENER, unlistener);
      return unlistener;
    }
    return null;
  },
  async [api.actions.clearAudits]({ commit }, payload) {
    // TODO: Die Reihenfolge der Ausführung im Componenten Lifecycle ist nicht nutzbar (OLD Create, New Create, OLD beforeDestroy, New beforeDestroy)
    commit(api.mutations.CLEAR_AUDITS, payload);
  },
  [api.actions.appendAttachmentToAudit](
    { commit, dispatch },
    { AuditId, Url, Name, BucketPath, FileUUID, Activity }
  ) {
    const attachment = {
      url: Url,
      bucket_path: BucketPath || null,
      id: FileUUID,
      name: Name
    };
    const payload = {
      audit_id: AuditId,
      activity: Activity,
      data: { attachments: { [FileUUID]: attachment } }
    };

    const audit_id = payload.audit_id;

    const ref = "audits";
    const doc = audit_id;
    const mutation = null;
    const child = null;

    Dispatcher.setAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch(
          "app/addStatus",
          { message: "Upload Successfull: " + Name, type: "success" },
          { root: true }
        );
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          { message: err.message, type: "error" },
          { root: true }
        );
      });
  },
  async [api.actions.updateAudit]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    payload = _.cloneDeep(payload);

    const audit_id = payload.audit_id;
    const activity = payload.activity;
    if (typeof payload.data.responsible === "undefined") {
      payload.data.responsible = null;
    }
    delete payload.data.audit_items;

    const ref = "audits";
    const doc = audit_id;
    const mutation = null;

    Dispatcher.updateAsync({ ref, doc, payload, commit, mutation })
      .then(() => {
        dispatch(
          "app/addStatus",
          { message: "Audit updated successfully", type: "success" },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        dispatch(
          "app/addStatus",
          { message: err.message, type: "error" },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });

    dispatch("activities/createActivity", activity, { root: true });
  },
  async [api.actions.updateAuditStatus]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    const audit_id = payload.audit_id;

    const ref = "audits";
    const doc = audit_id;
    const child = "workflow";
    const mutation = null;

    Dispatcher.updateAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });

    //dispatch("activities/createActivity", activity, { root: true });
  },
  async [api.actions.deleteAudit]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    const audit_id = payload.audit_id;

    const ref = "audits";
    const doc = audit_id;
    const mutation = null;

    Dispatcher.deleteAsync({ ref, doc, payload, commit, mutation })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });
  },
  async [api.actions.createAuditItem]({ commit, dispatch }, payload) {
    const audit_id = payload.audit_id;

    const data = payload.data;

    const ref = "audits";
    const doc = audit_id;
    const mutation = null;
    const child = "audit_items";

    const go = _.keyBy(data, () => {
      return uuid.v4();
    });
    payload = { ...payload, data: go };

    Dispatcher.setAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch(
          "app/addStatus",
          { message: "Audit item successfully created", type: "success" },
          { root: true }
        );
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          { message: err.message, type: "error" },
          { root: true }
        );
      });

    /*for (let key in data) {
      const my_data = { ...data[key] };
      const audititem_id = uuid.v4()
      const child = "audit_items." + audititem_id
      const payload = { audit_id: audit_id, audititem_id: audititem_id, data: my_data };

      Dispatcher.updateAsync({ ref, doc, child, payload, commit, mutation });
    }*/
  },
  async [api.actions.updateAuditItem]({ commit, dispatch }, payload) {
    const audit_id = payload.audit_id;
    const audititem_id = payload.audititem_id;

    const { ref, doc, child } = createAuditItemPath(audit_id, audititem_id);

    const mutation = null;

    Dispatcher.updateAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch(
          "app/addStatus",
          { message: "Audit item update successfull", type: "success" },
          { root: true }
        );
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          { message: err.message, type: "error" },
          { root: true }
        );
      });
  },
  async [api.actions.deleteAuditItem]({ commit, dispatch }, payload) {
    const audit_id = payload.audit_id;
    const audititem_id = payload.audititem_id;
    const { ref, doc, child } = createAuditItemPath(audit_id, audititem_id);
    const mutation = null;

    payload = { ...payload, data: firebase.firestore.FieldValue.delete() };

    Dispatcher.updateAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });
  },

  async [api.actions.uploadMedia](
    { commit },
    {
      ref: { auditId, auditItemId, findingId },
      file
    }
  ) {
    console.log("uploadMedia", auditId, auditItemId, findingId, file);

    let payload = {
      audit_id: auditId,
      audititem_id: auditItemId,
      deviation_id: findingId,
      media_id: uuid.v4(),
      file
    };

    const audit_id = payload.audit_id;
    const audititem_id = payload.audititem_id;
    const deviation_id = payload.deviation_id;
    const media_id = payload.media_id;

    let ref = "attachments";
    let child = child;
    let mutation = INVALID_MUTATION;
    //let newpayload = { ...payload };

    const downloadURL = await Dispatcher.uploadAsync({
      ref,
      child,
      payload,
      commit,
      mutation
    });

    const media_data = { url: downloadURL };

    payload = { data: media_data };

    ref = "audits";
    const doc = audit_id;
    child = fieldPath(
      "audit_items",
      audititem_id,
      "deviations",
      deviation_id,
      "media",
      media_id
    );
    await Dispatcher.updateAsync({
      ref,
      doc,
      child,
      payload,
      commit,
      mutation
    });
    return downloadURL;
  },
  async [api.actions.removeFileFromFinding]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    const audit_id = payload.audit_id;
    const audititem_id = payload.audititem_id;
    const deviation_id = payload.deviation_id;
    const media_id = payload.media_id;

    const ref = "audits";
    const doc = audit_id;
    const child =
      "audit_items." +
      audititem_id +
      ".deviations." +
      deviation_id +
      ".media." +
      media_id;

    const mutation = INVALID_MUTATION;
    payload = { ...payload, data: firebase.firestore.FieldValue.delete() };

    Dispatcher.updateAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });
  },
  async [api.actions.createMeasure]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    const measure_id = uuid.v4();

    payload = { ...payload, measure_id: measure_id };

    const ref = "measures";
    const doc = measure_id;
    const child = "";
    const mutation = "createMeasure";

    Dispatcher.createAsync({ ref, doc, child, payload, commit, mutation })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });
  },
  async [api.actions.updateMeasure]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    const measure_id = payload.measure_id;

    const ref = "measures";
    const doc = measure_id;
    const mutation = "updateMeasure";

    Dispatcher.updateAsync({ ref, doc, payload, commit, mutation, dispatch })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });
  },
  async [api.actions.deleteMeasure]({ commit, dispatch }, payload) {
    dispatch("app/setLoading", true, { root: true });
    const measure_id = payload.measure_id;

    const ref = "measures";
    const doc = measure_id;
    const mutation = "deleteMeasure";

    Dispatcher.deleteAsync({ ref, doc, payload, commit, mutation, dispatch })
      .then(() => {
        dispatch("app/setLoading", false, { root: true });
      })
      .catch(err => {
        console.error(err);
        dispatch(
          "app/addStatus",
          {
            message: err.message,
            type: "error"
          },
          { root: true }
        );
        dispatch("app/setLoading", false, { root: true });
      });
  }
};

const getters = {
  [api.getters.getDialogItem](state) {
    return state.currentDialogItem;
  },
  [api.getters.getDialogAction](state) {
    return state.currentDialogAction;
  },
  [api.getters.getAuditId](state) {
    return state.currentAuditId;
  },
  [api.getters.getAuditItemId](state) {
    return state.currentAuditItemId;
  },
  [api.getters.getDeviationId](state) {
    return state.currentDeviationId;
  },
  [api.getters.getMediaId](state) {
    return state.currentMediaId;
  },
  [api.getters.getMeasureId](state) {
    return state.currentMeasureId;
  },
  [api.getters.loadedAudits](state) {
    return _.map(state.loadedAudits);
  },
  [api.getters.createNewAudit]() {
    return () => {
      const now = new Date();
      return {
        workflow: {
          status: "1"
        },
        summary: null,
        type: "",
        standard: [],
        scope: [],
        process: "",
        planning_year: now.getFullYear(),
        planning_date: now.toISOString().substr(0, 10),
        name: "",
        members: {},
        coauditors: [],
        auditing_date: [],
        audited_by: "",

        audit_participants: [],
        audit_items: {}
      };
    };
  },

  [api.getters.getTodos](state, getters) {
    return (include, exclude, count) => {
      //TODO: MAXIMAL UNPERFORMANT
      const measures = getters.getMeasures;

      return measures
        .filter(measure => {
          for (let key in include) {
            if (measure[key] !== include[key]) {
              return false;
            }
          }

          for (let key in exclude) {
            if (measure[key] == exclude[key]) {
              return false;
            }
          }

          return true;
        })
        .sort((lhs, rhs) => {
          return String(lhs.dueDate).localeCompare(rhs.dueDate);
        })

        .slice(0, count);
    };
  },
  [api.getters.getFinalWorkflowStatusId](
    state,
    getters,
    rootState,
    rootGetter
  ) {
    const workflow = rootGetter["configuration/getWorkflowForComponent"](
      "audit"
    );

    let status = workflow.status.find(status => status.end);
    if (!status) {
      console.warn("Use last status as final state");
      status = workflow.status[workflow.status.length - 1];
    }
    return status.id;
  },
  [api.getters.getFinalMeasureWorkflowStatusId](
    state,
    getters,
    rootState,
    rootGetter
  ) {
    const workflow = rootGetter["configuration/getWorkflowForComponent"](
      "measure"
    );

    let status = workflow.status.find(status => status.end);
    if (!status) {
      console.warn("Use last measure status as final state");
      status = workflow.status[workflow.status.length - 1];
    }
    return status.id;
  },
  [api.getters.getAudits](state) {
    return (include, count) => {
      const extract_date = date => {
        if (date instanceof Array) {
          const nd = [...date];
          nd.sort();
          if (nd.length) {
            date = nd[0];
          } else {
            date = "9999-99-99";
          }
        } else if (typeof date !== "string") {
          date = "9999-99-99";
        }
        return date;
      };
      return Object.values(state.loadedAudits)
        .filter(audit => {
          return include.reduce(
            (prev, checker) => prev && checker(audit),
            true
          );
        })
        .sort((lhs, rhs) => {
          return extract_date(rhs.auditing_date).localeCompare(
            extract_date(lhs.auditing_date)
          );
        })
        .slice(0, count);
    };
  },
  [api.getters.loading](state) {
    return state.loading;
  },
  [api.getters.getDefaultCalcSchemaName](state, getters, rootState) {
    return rootState.configuration.audit.reportSchema || "compliance";
  },

  [api.getters.getFlatAuditMatrix](state, getters, rootState, rootGetters) {
    const workflow = rootGetters["configuration/getWorkflowForComponent"](
      "audit"
    );

    const resolver = user_resolver(rootGetters["users/getUser"]);
    const resolveUser = Data => resolver(Data).join(", ");

    const resolveStandard = Data => {
      if (typeof Data === "string") {
        Data = [Data];
      } else if (Data instanceof Array) {
        Data = [...Data];
      } else {
        Data = [];
      }

      return Data.map(standard_id => {
        const standard = rootGetters["standards/getStandard"](standard_id);
        if (standard) {
          return standard.name;
        } else {
          return null;
        }
      })
        .filter(val => val !== null)
        .join(", ");
    };

    const resolveDate = Data => {
      if (typeof Data === "string") {
        Data = [Data];
      } else if (Data instanceof Array) {
        Data = [...Data];
      } else {
        Data = [];
      }
      if (Data.length) {
        Data.sort();
        return Data[0];
      } else {
        return "";
      }
    };
    const resolveStatus = status_resolver(workflow);
    const extractStatus = Data => {
      let status = undefined;
      if (Data && Data.status) {
        status = Data.status;
      }
      return status;
    };
    return _.map(state.loadedAudits, (audit, id) => {
      let leadauditor = resolveUser(audit.leadauditor);
      if (leadauditor === "") {
        leadauditor = "(leer)";
      }
      let standard = resolveStandard(audit.standard);
      if (standard === "") {
        standard = "(leer)";
      }

      return {
        id,
        protected: audit.protected || false,
        name: audit.name || "",
        title: audit.name || "",
        standard,
        type: audit.type || "",
        audited_by: audit.audited_by || "",
        scope: audit.scope || "",
        auditor: resolveUser(audit.leadauditor) + resolveUser(audit.coauditors),
        coauditors: resolveUser(audit.coauditors),
        leadauditor,
        planning_year: String(audit.planning_year || 2019),
        auditing_date: resolveDate(audit.auditing_date),
        planning_date: resolveDate(audit.planning_date),
        auditing_date_display: audit.auditing_date,
        planning_date_display: audit.planning_date,
        status: resolveStatus(extractStatus(audit.workflow)),
        hasPendingWrites: audit.hasPendingWrites,
        fromCache: audit.fromCache
      };
    });
  },
  [api.getters.getFlatMeasureMatrix](state, getters, rootState, rootGetters) {
    const workflow = rootGetters["configuration/getWorkflowForComponent"](
      "measure"
    );
    const resolver = user_resolver(rootGetters["users/getUser"]);
    const resolveUser = Data => resolver(Data).join(", ");

    const resolveStatus = status_resolver(workflow);

    return auditId => {
      let measures = [];
      if (auditId === null) {
        measures = getters.getMeasures;
      } else {
        measures = getters.getMeasuresByAuditId(auditId);
      }
      console.log("getFlatMeasureMatrix", auditId, measures);
      return measures.map(measure => {
        let assignedTo = resolveUser(measure.assignedTo);
        if (assignedTo === "") {
          assignedTo = "(leer)";
        }
        return {
          id: measure.id,
          text: measure.text || "",
          autor: resolveUser(measure.autor),
          audit_name: measure.audit_name || "",
          assignedTo,
          status: resolveStatus(measure.status),
          dueDate: measure.dueDate || "",
          createdDate: measure.createdDate || "",

          audit_id: measure.audit_id || "",
          audititem_id: measure.audititem_id || "",
          deviation_id: measure.deviation_id || ""
        };
      });
    };
  },
  [api.getters.getMeasures](state) {
    return Object.values(state.loadedMeasures);
  },

  [api.getters.getMeasuresByAuditId]: (state, getters) => auditId => {
    console.log("getMeasuresByAuditId", auditId);
    const result = getters.getMeasures.filter(
      measure => measure.audit_id === auditId
    );

    return result;
  },
  [api.getters.getMappedMeasures]: (state, getters) => {
    const result = _.groupBy(getters.getMeasures, "id");

    return result;
  },
  [api.getters.getMeasureById]: (state, getters) => id => {
    // TODO: Sehr unschön und fehleranfällig!
    const result = getters.getMappedMeasures[id][0] || {
      id,
      loadingState: "FAILED"
    };

    return result;
  },
  [api.getters.getMappedAudits]: (state, getters) => {
    const result = _.groupBy(getters.getAudits, _.property("id"));

    return result;
  },
  [api.getters.getMappedFindings]: (state, getters) => {
    const result = _.groupBy(getters.getAudits, _.property("id"));

    return result;
  },
  [api.getters.getAuditById]: state => id => {
    console.log("getAuditById", id);
    const result = (state.loadedAudits || {})[id];

    return result;
  },
  [api.getters.getAuditFindingMapping]: (state, getters) => {
    const mapping = {};
    _.each(getters.getAudits, ({ id, audit_items }) => {
      mapping[id] = _.flatMap(audit_items, ({ deviations }) =>
        _.mapKeys(deviations)
      );
    });

    return mapping;
  },
  [api.getters.getFindingMeasureMapping]: (state, getters) => {
    const result = _.groupBy(getters.getMeasures, "deviation_id");
    return result;
  },
  [api.getters.getMeasureIdsByFindingId]: (state, getters) => finding_id => {
    const result = getters.getFindingMeasureMapping[finding_id];

    return result;
  }
};

export default {
  namespaced: true,
  getters,
  state,
  mutations,
  actions
};
